import { Container, Toaster, TooltipProvider } from '@helios/ui';
import { Sidebar } from '@/components/layout/Sidebar';
import { SettingsRail } from '@/components/layout/SettingsRail';
import { TopBar } from '@/components/layout/TopBar';
import { SystemStatusBar } from '@/components/layout/SystemStatusBar';
import { DemoEnvironmentBanner } from '@/components/layout/DemoEnvironmentBanner';
import { MobileNav } from '@/components/layout/MobileNav';
import { HeliosChatPanel } from '@/components/layout/AiAdvisorDrawer';
import { HubAwareOutlet } from '@/components/layout/HubAwareOutlet';
import { RouteTransition } from '@/components/layout/RouteTransition';
import { DataSurfaceProvider } from '@/components/layout/DataSurfaceProvider';
import { CommandPalette } from '@/components/command-palette/CommandPalette';
import { useCommandShortcut } from '@/hooks/useCommandShortcut';
import { useGlassSpotlight } from '@/hooks/useGlassSpotlight';
import { useVendorDataRealtime } from '@/hooks/useVendorDataRealtime';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { useLocation } from 'react-router';
import { useCallback, useRef, type CSSProperties } from 'react';

/**
 * The HELIOS application shell: persistent left navigation, top command bar,
 * main content grid, Helios AI panel, and the system status dock.
 * Provides the accessibility baseline — skip link, semantic landmarks, tooltip
 * provider and a global toast host.
 */
export function AppShell() {
  const advisorLauncher = useRef<HTMLButtonElement>(null);
  const returnAdvisorFocus = useCallback(() => {
    const launcher = advisorLauncher.current;
    if (launcher?.isConnected && !launcher.disabled) launcher.focus({ preventScroll: true });
  }, []);
  useCommandShortcut();
  useGlassSpotlight();
  useVendorDataRealtime();
  const { preferences } = useUserPreferences();
  const { pathname } = useLocation();
  const isSettings = pathname === '/settings' || pathname.startsWith('/settings/');
  const comfortable = preferences?.denseMode === false;
  const densityStyle = comfortable
    ? ({
        '--helios-control-height': '36px',
        '--helios-input-height': '36px',
        '--helios-button-height': '36px',
        '--helios-button-sm-height': '36px',
        '--helios-button-xs-height': '36px',
        '--helios-button-lg-height': '52px',
        '--helios-button-icon-size': '36px',
      } as CSSProperties)
    : undefined;

  return (
    <DataSurfaceProvider>
      <TooltipProvider delayDuration={300} skipDelayDuration={200}>
        <div
          className="cos-app-shell text-foreground flex h-screen max-h-[100dvh] w-full overflow-hidden"
          data-ui-density={comfortable ? 'comfortable' : 'dense'}
          style={densityStyle}
        >
          <a
            href="#main-content"
            className="z-toast bg-primary text-primary-foreground sr-only rounded-lg px-3 py-2 text-sm font-medium focus:not-sr-only focus:absolute focus:left-4 focus:top-3"
          >
            Skip to content
          </a>

          <Sidebar />
          <SettingsRail />

          <div className="relative z-10 flex min-h-0 min-w-0 flex-1 flex-col">
            <TopBar advisorLauncherRef={advisorLauncher} />
            <DemoEnvironmentBanner />
            <main
              id="main-content"
              tabIndex={-1}
              className={`flex min-h-0 flex-1 flex-col overflow-hidden focus:outline-none ${isSettings ? '' : 'max-lg:overflow-y-auto'}`}
            >
              <Container className="flex h-full min-h-0 flex-1 flex-col py-2">
                <RouteTransition>
                  <HubAwareOutlet />
                </RouteTransition>
              </Container>
            </main>
            <SystemStatusBar />
          </div>

          <MobileNav />
          <HeliosChatPanel onReturnFocus={returnAdvisorFocus} />
          <CommandPalette />
          <Toaster />
        </div>
      </TooltipProvider>
    </DataSurfaceProvider>
  );
}
